/**
 * 清新薄荷模版 - 页脚
 * 
 * 设计理念：与红色模版的3栏紧凑布局形成差异
 * - 4栏网格，更现代的信息架构
 * - 顶部渐变线替代色块
 * - 更轻量的灰色调
 * - 品牌区带描述文字
 */

import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function TealFooter() {
  const { siteConfig } = useSiteConfig();

  const phone = siteConfig?.contact_info?.phone || '';
  const email = siteConfig?.contact_info?.email || '';
  const address = siteConfig?.contact_info?.address;
  const companyName = siteConfig?.contact_info?.company_name;
  const icp = siteConfig?.icp || '';
  const icpUrl = siteConfig?.icp_url || '';
  const displayName = siteConfig?.site_name || '';
  const siteName = siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const footerInfo = siteConfig?.footer_info || '';

  const defaultMenus = [
    { id: "m1", label: "首页", href: "/", external: false, enabled: true },
    { id: "m2", label: "发布信息", href: "/publish", external: false, enabled: true },
    { id: "m3", label: "开通VIP", href: "/vip", external: false, enabled: true },
    { id: "m4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
    { id: "m5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
    { id: "m6", label: "关于我们", href: "/page/about", external: false, enabled: true },
    { id: "m7", label: "隐私政策", href: "/page/privacy", external: false, enabled: true },
    { id: "m8", label: "服务协议", href: "/page/register-agreement", external: false, enabled: true },
  ];

  const hasFooterMenus = siteConfig?.footer_menus && Array.isArray(siteConfig.footer_menus) && siteConfig.footer_menus.length > 0;
  const menus = hasFooterMenus
    ? siteConfig.footer_menus.filter((m: any) => m.enabled !== false)
    : defaultMenus;

  const currentYear = new Date().getFullYear();
  const copyrightOwner = companyName || displayName || siteName;
  const copyright = copyrightOwner ? `\u00a9 ${currentYear} ${copyrightOwner}` : `\u00a9 ${currentYear}`;

  const brandName = displayName || siteName;

  // 将菜单分成两组：第一组4个，第二组4个
  const col1Menus = menus.slice(0, 4);
  const col2Menus = menus.slice(4, 8);

  return (
    <footer className="bg-[#1a1f2e] text-gray-400">
      {/* 渐变顶线 */}
      <div className="h-1 bg-gradient-to-r from-teal-400 via-emerald-500 to-teal-600" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">

          {/* 栏目1: 品牌介绍 */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              {logo ? (
                <img src={logo} alt={brandName} className="h-8 w-8 object-contain rounded-lg" />
              ) : (
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                  {brandName ? brandName.charAt(0) : 'S'}
                </div>
              )}
              {brandName && (
                <span className="text-lg font-bold text-white">{brandName}</span>
              )}
            </div>
            {footerInfo ? (
              <p className="text-sm leading-relaxed">{footerInfo}</p>
            ) : (
              <p className="text-sm leading-relaxed">
                本地分类信息平台，汇聚各类生活服务信息，免费发布、便捷查找。
              </p>
            )}
            {/* 社交媒体图标 */}
            <div className="flex items-center gap-3 mt-5">
              {phone && (
                <a href={`tel:${phone}`} className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-teal-500/20 hover:text-teal-400 transition-colors" title="电话联系">
                  <i className="ri-phone-line" />
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-teal-500/20 hover:text-teal-400 transition-colors" title="发送邮件">
                  <i className="ri-mail-line" />
                </a>
              )}
            </div>
          </div>

          {/* 栏目2: 快捷链接 第一组 */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">快捷导航</h4>
            <ul className="space-y-3">
              {col1Menus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm hover:text-teal-400 transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm hover:text-teal-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 栏目3: 快捷链接 第二组 */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">更多服务</h4>
            <ul className="space-y-3">
              {col2Menus.length > 0 ? col2Menus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm hover:text-teal-400 transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm hover:text-teal-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              )) : (
                <li className="text-sm text-gray-500">暂无更多</li>
              )}
            </ul>
          </div>

          {/* 栏目4: 联系方式 */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">联系我们</h4>
            <ul className="space-y-3">
              {phone && (
                <li>
                  <a href={`tel:${phone}`}
                    className="flex items-center gap-2.5 text-sm hover:text-teal-400 transition-colors">
                    <i className="ri-phone-line text-teal-400 text-base" />
                    {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={`mailto:${email}`}
                    className="flex items-center gap-2.5 text-sm hover:text-teal-400 transition-colors">
                    <i className="ri-mail-line text-teal-400 text-base" />
                    {email}
                  </a>
                </li>
              )}
              {address && (
                <li>
                  <div className="flex items-start gap-2.5 text-sm">
                    <i className="ri-map-pin-line text-teal-400 text-base mt-0.5 shrink-0" />
                    <span>{address}</span>
                  </div>
                </li>
              )}
              {!phone && !email && !address && (
                <li className="text-sm text-gray-500">暂无联系方式</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* 底栏 */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-gray-500">{copyright}</span>
          <div className="flex items-center gap-4">
            {icp && (
              <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer"
                className="text-xs text-gray-500 hover:text-teal-400 transition-colors">
                {icp}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
